import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Brain,
  CheckCircle2,
  Clock,
  FolderKanban,
  Lightbulb,
  Star,
  TrendingUp,
  AlertTriangle,
  Eye,
  Play,
  Loader2,
  ShieldCheck,
  Search,
  ExternalLink,
  RefreshCw,
  Download,
} from "lucide-react";
import { useExportMetrics } from "@/hooks/useExportMetrics";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { DIMENSION_NAMES } from "@/data/liderIAEvaluationData";
import { OrganizationFilter, useOrganizationFilter } from "@/components/admin/OrganizationFilter";
import {
  useProjectEvaluations,
  useEvaluateProject,
  useMarkAsReviewed,
  getEvaluationEligibility,
  type EvaluationWithProject,
  type ComponentScore,
} from "@/hooks/useProjectEvaluations";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCohortFilterStore, useOrgFilterStore } from '@/hooks/useOrgFilterStore';

type ProjectStatus = "pending" | "evaluated" | "reviewed";

const getClassificationColor = (classification: string) => {
  switch (classification) {
    case "Quick Win": return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
    case "Strategic Play": return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
    case "Experiment": return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
    case "Back-log": return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
    default: return "";
  }
};

export default function AIEvaluationsPage() {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<"all" | ProjectStatus>("all");
  const [selectedCohort, setSelectedCohort] = useCohortFilterStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState<EvaluationWithProject | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [evaluatingId, setEvaluatingId] = useState<string | null>(null);
  const [evaluatingAll, setEvaluatingAll] = useState(false);
  const [selectedOrg, setSelectedOrg] = useOrgFilterStore();
  const { isSuperAdmin } = useOrganizationFilter(selectedOrg);
  const { exportToExcel } = useExportMetrics();

  const evaluateProject = useEvaluateProject();
  const markAsReviewed = useMarkAsReviewed();

  const { data: allItems = [], isLoading } = useProjectEvaluations({
    organizationId: selectedOrg !== "all" ? selectedOrg : undefined,
  });

  // Get cohorts for filter — synced with org
  const { data: cohorts = [] } = useQuery({
    queryKey: ["cohorts-for-eval-filter", selectedOrg],
    queryFn: async () => {
      let query = supabase.from("cohorts").select("id, name").order("name");
      if (selectedOrg !== "all") {
        query = query.eq("organization_id", selectedOrg);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });

  // Reset cohort when org changes
  const handleOrgChange = (orgId: string) => {
    setSelectedOrg(orgId);
    setSelectedCohort("all");
  };

  // Derive status for each item
  const getItemStatus = (item: EvaluationWithProject): ProjectStatus => {
    return (item.evaluation?.status as ProjectStatus) || "pending";
  };

  const filteredItems = allItems.filter(item => {
    const status = getItemStatus(item);
    const matchesStatus = selectedStatus === "all" || status === selectedStatus;
    const matchesCohort = selectedCohort === "all" || item.project.cohort_id === selectedCohort;
    const matchesSearch = searchTerm === "" ||
      item.project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.participant?.full_name || "").toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesCohort && matchesSearch;
  });

  const stats = {
    total: allItems.length,
    pending: allItems.filter(i => getItemStatus(i) === "pending").length,
    evaluated: allItems.filter(i => getItemStatus(i) === "evaluated").length,
    reviewed: allItems.filter(i => getItemStatus(i) === "reviewed").length,
    avgScore: (() => {
      const scored = allItems.filter(i => i.evaluation?.total_score != null);
      return scored.length > 0
        ? Math.round(scored.reduce((acc, i) => acc + (i.evaluation!.total_score || 0), 0) / scored.length)
        : 0;
    })(),
  };

  const pendingItems = allItems.filter(i => getItemStatus(i) === "pending");

  const isItemEligible = (item: EvaluationWithProject) =>
    getEvaluationEligibility({
      submitted: (item.project as any).submitted,
      status: item.project.status,
      rubric_answers: item.project.rubric_answers as Record<string, string> | null,
    });

  const handleEvaluate = async (item: EvaluationWithProject) => {
    setEvaluatingId(item.project.id);
    try {
      await evaluateProject.mutateAsync(item.project.id);
      toast.success(`"${item.project.name}" evaluado por IA`);
    } catch (err: any) {
      toast.error(err.message || "Error al evaluar");
    } finally {
      setEvaluatingId(null);
    }
  };

  const evaluateAllPending = async () => {
    const eligible = pendingItems.filter((i) => isItemEligible(i).eligible);
    const skipped = pendingItems.length - eligible.length;
    if (eligible.length === 0) {
      toast.info(
        pendingItems.length === 0
          ? "No hay proyectos pendientes"
          : `Ninguno de los ${pendingItems.length} pendientes está listo para evaluar (falta entregable o rúbrica)`,
      );
      return;
    }
    setEvaluatingAll(true);
    for (const item of eligible) {
      await handleEvaluate(item);
    }
    setEvaluatingAll(false);
    toast.success(
      skipped > 0
        ? `${eligible.length} evaluados. ${skipped} omitidos por no estar listos.`
        : `${eligible.length} proyectos evaluados`,
    );
  };

  const handleMarkReviewed = async (projectId: string) => {
    try {
      await markAsReviewed.mutateAsync(projectId);
      toast.success("Proyecto marcado como revisado");
    } catch (err: any) {
      toast.error(err.message || "Error al marcar como revisado");
    }
  };

  const openDetail = (item: EvaluationWithProject) => {
    setSelectedItem(item);
    setDetailOpen(true);
  };

  const getScoreColor = (score: number, max: number = 500) => {
    const pct = (score / max) * 100;
    if (pct >= 80) return "text-green-600";
    if (pct >= 60) return "text-amber-600";
    return "text-red-600";
  };

  const getStatusBadge = (status: ProjectStatus) => {
    switch (status) {
      case "pending":
        return (
          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 gap-1">
            <Clock className="h-3 w-3" /> Pendiente
          </Badge>
        );
      case "evaluated":
        return (
          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 gap-1">
            <Brain className="h-3 w-3" /> Evaluado IA
          </Badge>
        );
      case "reviewed":
        return (
          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 gap-1">
            <ShieldCheck className="h-3 w-3" /> Revisado
          </Badge>
        );
    }
  };

  const groupByDimension = (scores: ComponentScore[]) => {
    const groups: Record<string, ComponentScore[]> = {};
    scores.forEach(s => {
      if (!groups[s.dimension]) groups[s.dimension] = [];
      groups[s.dimension].push(s);
    });
    return groups;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold flex items-center gap-3">
            <Brain className="h-8 w-8 text-primary" />
            Evaluaciones de Proyectos
          </h1>
          <p className="text-muted-foreground mt-1">
            Revisa las evaluaciones IA de proyectos y márcalos como revisados
          </p>
        </div>
        <div className="flex items-center gap-2">
          <OrganizationFilter selectedOrg={selectedOrg} onOrgChange={handleOrgChange} />
          <Button
            variant="outline"
            onClick={() => {
              const rows = filteredItems.map(item => ({
                proyecto: item.project.name,
                participante: item.participant?.full_name || "—",
                empresa: item.participant?.company || "—",
                cohorte: item.cohort_name || "—",
                estado: getItemStatus(item),
                score: item.evaluation?.total_score ?? "—",
                clasificacion: item.evaluation?.classification || "—",
                fecha_evaluacion: item.evaluation?.evaluated_at ? new Date(item.evaluation.evaluated_at).toLocaleDateString("es-ES") : "—",
              }));
              exportToExcel(rows, [
                { key: "proyecto", header: "Proyecto" },
                { key: "participante", header: "Participante" },
                { key: "empresa", header: "Empresa" },
                { key: "cohorte", header: "Cohorte" },
                { key: "estado", header: "Estado" },
                { key: "score", header: "Score" },
                { key: "clasificacion", header: "Clasificación" },
                { key: "fecha_evaluacion", header: "Fecha Evaluación" },
              ], { fileName: "evaluaciones-proyectos", title: "Evaluaciones de Proyectos" });
            }}
            className="gap-2"
            disabled={filteredItems.length === 0}
          >
            <Download className="h-4 w-4" /> Exportar
          </Button>
          <Button
            onClick={evaluateAllPending}
            disabled={evaluatingAll || pendingItems.length === 0}
            className="gap-2"
          >
            {evaluatingAll ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Evaluando...</>
            ) : (
              <><Play className="h-4 w-4" /> Evaluar Pendientes ({stats.pending})</>
            )}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <FolderKanban className="h-5 w-5 text-muted-foreground" />
              <span className="text-2xl font-bold">{stats.total}</span>
            </div>
            <p className="text-sm text-muted-foreground">Total Proyectos</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              <span className="text-2xl font-bold text-amber-600">{stats.pending}</span>
            </div>
            <p className="text-sm text-muted-foreground">Pendientes</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold text-blue-600">{stats.evaluated}</span>
            </div>
            <p className="text-sm text-muted-foreground">Evaluados IA</p>
          </CardContent>
        </Card>
        <Card className="bg-green-500/5 border-green-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold text-green-600">{stats.reviewed}</span>
            </div>
            <p className="text-sm text-muted-foreground">Revisados</p>
          </CardContent>
        </Card>
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 text-primary" />
              <span className="text-2xl font-bold text-primary">{stats.avgScore}/500</span>
            </div>
            <p className="text-sm text-muted-foreground">Score Promedio</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters + Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="text-lg">Proyectos</CardTitle>
              <CardDescription>{filteredItems.length} proyectos encontrados</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar proyecto o participante..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10 w-[220px]"
                />
              </div>
              <Select value={selectedStatus} onValueChange={(v) => setSelectedStatus(v as any)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="evaluated">Evaluados IA</SelectItem>
                  <SelectItem value="reviewed">Revisados</SelectItem>
                </SelectContent>
              </Select>
              <SearchableCombobox
                value={selectedCohort}
                onSelect={setSelectedCohort}
                placeholder="Cohorte"
                searchPlaceholder="Buscar cohorte..."
                triggerClassName="w-[200px]"
                options={[
                  { value: 'all', label: 'Todas las cohortes', keywords: ['todas','todos'] },
                  ...cohorts.map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                     <TableHead>Proyecto</TableHead>
                     <TableHead>Participante</TableHead>
                     <TableHead className="hidden md:table-cell">Empresa</TableHead>
                     <TableHead className="hidden md:table-cell">Cohorte</TableHead>
                     <TableHead className="text-center">Estado</TableHead>
                     <TableHead className="text-center">Score</TableHead>
                     <TableHead className="text-center hidden sm:table-cell">Clasificación</TableHead>
                     <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No se encontraron proyectos con los filtros seleccionados
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map(item => {
                      const status = getItemStatus(item);
                      const score = item.evaluation?.total_score;
                      const classification = item.evaluation?.classification;
                      return (
                        <TableRow key={item.project.id}>
                          <TableCell className="font-medium max-w-[200px]">
                            <div>
                              <p className="truncate">{item.project.name}</p>
                              <p className="text-xs text-muted-foreground">{item.participant?.area || "—"}</p>
                            </div>
                          </TableCell>
                          <TableCell>{item.participant?.full_name || "—"}</TableCell>
                          <TableCell className="text-muted-foreground hidden md:table-cell">{item.participant?.company || "—"}</TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Badge variant="secondary" className="font-normal">{item.cohort_name || "Sin cohorte"}</Badge>
                          </TableCell>
                          <TableCell className="text-center hidden sm:table-cell">
                            {getStatusBadge(status)}
                          </TableCell>
                          <TableCell className="text-center">
                            {score != null ? (
                              <span className={`font-bold ${getScoreColor(score)}`}>
                                {score}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {classification ? (
                              <Badge className={getClassificationColor(classification)}>
                                {classification}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {(() => {
                                const elig = isItemEligible(item);
                                const showEvaluate = status === "pending";
                                const showReEvaluate = status === "evaluated" || status === "reviewed";
                                if (!showEvaluate && !showReEvaluate) return null;
                                const btn = (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEvaluate(item)}
                                    disabled={evaluatingId === item.project.id || evaluatingAll || !elig.eligible}
                                    className="gap-1"
                                  >
                                    {evaluatingId === item.project.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : showEvaluate ? (
                                      <Brain className="h-3 w-3" />
                                    ) : (
                                      <RefreshCw className="h-3 w-3" />
                                    )}
                                    {showEvaluate ? "Evaluar" : "Re-evaluar"}
                                  </Button>
                                );
                                return elig.eligible ? btn : (
                                  <span title={elig.reason || "No elegible"} className="inline-flex">{btn}</span>
                                );
                              })()}
                              {status === "evaluated" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleMarkReviewed(item.project.id)}
                                  className="gap-1 text-green-600 border-green-300 hover:bg-green-50"
                                >
                                  <CheckCircle2 className="h-3 w-3" />
                                  Revisar
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDetail(item)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Ver
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => navigate(`/ai-portfolio/${item.project.id}`)}
                                title="Ver detalle completo"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedItem && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  {getStatusBadge(getItemStatus(selectedItem))}
                  {selectedItem.evaluation?.classification && (
                    <Badge className={getClassificationColor(selectedItem.evaluation.classification)}>
                      {selectedItem.evaluation.classification}
                    </Badge>
                  )}
                </div>
                <DialogTitle className="text-xl mt-2">{selectedItem.project.name}</DialogTitle>
                <DialogDescription>
                  {selectedItem.participant?.full_name || "—"} • {selectedItem.participant?.company || "—"} • {selectedItem.cohort_name || "Sin cohorte"} • {selectedItem.participant?.area || "—"}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Project Info (always shown) */}
                {(!selectedItem.evaluation?.component_scores) && (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30">
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-400 flex items-center gap-2">
                        <Clock className="h-4 w-4" /> Este proyecto aún no ha sido evaluado por IA
                      </p>
                    </div>
                    {selectedItem.project.problem && (
                      <div><p className="text-xs text-muted-foreground font-medium mb-1">Problema</p><p className="text-sm">{selectedItem.project.problem}</p></div>
                    )}
                    {selectedItem.project.solution_description && (
                      <div><p className="text-xs text-muted-foreground font-medium mb-1">Solución</p><p className="text-sm">{selectedItem.project.solution_description}</p></div>
                    )}
                    {selectedItem.project.kpi_name && (
                      <div><p className="text-xs text-muted-foreground font-medium mb-1">KPI</p><p className="text-sm">{selectedItem.project.kpi_name}: {selectedItem.project.kpi_current_value || "—"} → {selectedItem.project.kpi_target_value || "—"}</p></div>
                    )}
                    {(() => {
                      const elig = isItemEligible(selectedItem);
                      return (
                        <div className="space-y-2">
                          <Button onClick={() => handleEvaluate(selectedItem)} disabled={evaluatingId === selectedItem.project.id || !elig.eligible} className="gap-2">
                            {evaluatingId === selectedItem.project.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
                            Evaluar con IA
                          </Button>
                          {!elig.eligible && (
                            <p className="text-xs text-amber-600">⚠ {elig.reason}</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {selectedItem.evaluation?.component_scores && (
                  <>
                {/* Score Summary */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border">
                  <div className="flex items-center gap-3">
                    <Star className="h-6 w-6 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Score Total LIDER-IA</p>
                      <p className={`text-3xl font-bold ${getScoreColor(selectedItem.evaluation.total_score!)}`}>
                        {selectedItem.evaluation.total_score} <span className="text-lg text-muted-foreground font-normal">/ 500</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedItem.project.file_url && (
                      <Button variant="outline" size="sm" onClick={() => window.open(selectedItem.project.file_url!, '_blank')} className="gap-1">
                        <Download className="h-3 w-3" />
                        Entregable
                      </Button>
                    )}
                    {(() => {
                      const elig = isItemEligible(selectedItem);
                      const btn = (
                        <Button variant="outline" size="sm" onClick={() => handleEvaluate(selectedItem)} disabled={evaluatingId === selectedItem.project.id || !elig.eligible} className="gap-1">
                          {evaluatingId === selectedItem.project.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          Re-evaluar
                        </Button>
                      );
                      return elig.eligible ? btn : <span title={elig.reason || "No elegible"} className="inline-flex">{btn}</span>;
                    })()}
                    {getItemStatus(selectedItem) === "evaluated" && (
                      <Button onClick={() => handleMarkReviewed(selectedItem.project.id)} className="gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        Marcar como Revisado
                      </Button>
                    )}
                  </div>
                </div>

                {/* Component Scores by Dimension */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Evaluación por Componente LIDER-IA
                  </h4>
                  {Object.entries(groupByDimension(selectedItem.evaluation.component_scores)).map(([dim, scores]) => (
                    <div key={dim} className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-bold">{dim}</Badge>
                        <span className="text-sm font-medium text-muted-foreground">
                          {DIMENSION_NAMES[dim as keyof typeof DIMENSION_NAMES] || dim}
                        </span>
                      </div>
                      <div className="space-y-3 ml-4">
                        {scores.map((cs, idx) => (
                          <div key={idx} className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">{cs.name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">Peso: {cs.weight}%</span>
                                <span className={`text-sm font-bold ${getScoreColor(cs.score, 5)}`}>
                                  {cs.score.toFixed(1)}/5
                                </span>
                              </div>
                            </div>
                            <Progress value={(cs.score / 5) * 100} className="h-1.5" />
                            <p className="text-xs text-muted-foreground">{cs.feedback}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* General Comments */}
                {selectedItem.evaluation.general_comments && (
                  <div className="space-y-2">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      Comentarios de la IA
                    </h4>
                    <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                      {selectedItem.evaluation.general_comments}
                    </p>
                  </div>
                )}

                {/* Suggestions */}
                {selectedItem.evaluation.suggestions && selectedItem.evaluation.suggestions.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Lightbulb className="h-4 w-4 text-amber-500" />
                      Sugerencias de Mejora
                    </h4>
                    <ul className="space-y-2">
                      {selectedItem.evaluation.suggestions.map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm bg-amber-50 dark:bg-amber-900/10 p-3 rounded-lg border border-amber-200 dark:border-amber-800/30">
                          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Metadata */}
                <div className="text-xs text-muted-foreground pt-4 border-t flex flex-wrap gap-4">
                  {selectedItem.project.submitted_at && (
                    <span>Enviado: {new Date(selectedItem.project.submitted_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}</span>
                  )}
                  {selectedItem.evaluation?.evaluated_at && (
                    <span>Evaluado IA: {new Date(selectedItem.evaluation.evaluated_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}</span>
                  )}
                  {selectedItem.evaluation?.reviewed_at && (
                    <span>Revisado: {new Date(selectedItem.evaluation.reviewed_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}</span>
                  )}
                </div>
                  </>
                )}
              </div>

              {/* Link to full detail */}
              <div className="pt-2 border-t flex justify-end gap-2">
                <Button variant="outline" size="sm" className="gap-2" onClick={() => { setDetailOpen(false); navigate(`/ai-portfolio/${selectedItem.project.id}`); }}>
                  <ExternalLink className="h-4 w-4" />
                  Ver detalle completo
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
